import React, {

    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from "react";
import { formatValueAsDateAndTime } from "../../../helpers/utils/DateUtils";
import DiffViewer, { DiffMethod } from 'react-diff-viewer-continued';
import Modal from "../../../components/common/form/Modal";
import { DataTableColumn } from "../../../components/common/datatable/DataTableTypes";
import { DataTable } from "../../../components/common/datatable/DataTable";
import { useSearchOrderResulHistoryByProductIdQuery } from "../productsApi";
import { createFormattedDate } from "../../../helpers/utils/Utils";

interface OrderResult {
    id: string;
    creationDate: string;
    createdBy: string;
    orderResult?: Record<string, any>;
}

interface ReadDiffViewerModalProps {
    show: boolean;
    setShow: (value: boolean) => void;
    productId: any;
}

export const ReadDiffViewerModal: React.FC<ReadDiffViewerModalProps> = ({ show, setShow, productId }) => {

    const orderResultData = useSearchOrderResulHistoryByProductIdQuery({ productId: productId });

    const [newOrderResult, setNewOrderResult] = useState<OrderResult | null>(null);
    const [oldOrderResult, setOldOrderResult] = useState<OrderResult | null>(null);

    const rowRefs = useRef<Record<string, HTMLTableRowElement>>({});


    useLayoutEffect(() => {
        if (!orderResultData.data) {
            return;
        }

        // Explicitly type the selectedRowClass object
        const selectedRowClass: Record<string, string> = {
            [newOrderResult?.id ?? ""]: "selected_row_result_history_new !cursor-pointer",
            [oldOrderResult?.id ?? ""]: "selected_row_result_history_old !cursor-pointer",
        };

        // Use properly typed history
        orderResultData.data.data.forEach((history: any) => {
            const rowForUser = rowRefs.current[history.id];
            if (!rowForUser) {
                return;
            }

            rowForUser.classList.remove(
                "selected_row_result_history_new",
                "selected_row_result_history_old"
            );

            const selectedClass = selectedRowClass[history.id];
            if (selectedClass) {
                rowForUser.classList.add(selectedClass);
            }
        });
    }, [newOrderResult, oldOrderResult, orderResultData.data]);

    function selectOrderResults(props: any) {
        const handleClick = (e: React.MouseEvent) => {
            e.preventDefault();

            const index = props.data.findIndex((cur: OrderResult) => cur.id === props.row.original.id);
            if (props.data.length > 1) {
                if (props.data.length === index + 1) {
                    setOldOrderResult(null);
                } else {
                    setOldOrderResult(props.data[index + 1]);
                }
            }
            setNewOrderResult(props.row.original);
        };

        const formattedDate = formatValueAsDateAndTime(props.row.original.creationDate, 'MM/DD/YY h:mm:ss a');

        return (
            <span
                id={props.row.original.id}
                title={formattedDate as string}
                className="formatted_date cursor"
                ref={(el: HTMLSpanElement | null) => {
                    if (el) {
                        const tr = el.closest("tr") as HTMLTableRowElement;
                        tr.classList.add("cursor-pointer")

                        tr.addEventListener("click", (e: MouseEvent) => {
                            e.preventDefault();
                            const index = props.data.findIndex((cur: any) => cur.id === props.row.original.id);
                            if (props.data.length > 1) {
                                if (props.data.length === index + 1) {
                                    setOldOrderResult(null);
                                } else {
                                    setOldOrderResult(props.data[index + 1]);
                                }
                            }
                            setNewOrderResult(props.row.original);
                        });
                    }
                }}
            >
                {formattedDate}
            </span>
        );
    }

    const columns: DataTableColumn[] = [
        {
            label: "Time",
            accessor: "creationDate",
            canSort: false,
            cell: (props: any) => selectOrderResults(props),
        },
        {
            label: "Editor",
            accessor: "createdBy",
            canSort: false,
        }
    ]

    const customFilter = useMemo(() => ({ productId }), [productId]);

    function getFormattedValue(orderResults: OrderResult | null): string {
        if (!orderResults?.orderResult) return "";
        const cleanedString = orderResults.orderResult.trim();
        const parsed = JSON.parse(cleanedString);
        return JSON.stringify(parsed, null, 2);
    }

    const leftTitle = newOrderResult !== null
        ? createFormattedDate(newOrderResult?.creationDate, 'MM/DD/YY h:mm:ss a')
        : "No Result Selected";

    const rightTitle = oldOrderResult !== null ? createFormattedDate(oldOrderResult?.creationDate, 'MM/DD/YY h:mm:ss a')
        : "No Result Selected";

    return (
        <Modal
            title={"Timeline"}
            show={show}
            setShow={setShow}
            className={"w-3/4"}
            fields={
                <>
                    <div className="modal-body">
                        <p className="ml-1">Select the Results you would like to compare</p>
                        <div className="d-flex flex-row gap-3">
                            {/* Left Column - Table */}
                            <div className="flex-shrink-0" style={{ flexBasis: "35%" }}>
                                <div className="table_data h-100">
                                    <div className="table-responsive jurisdictions_selector">
                                        <DataTable
                                            columns={columns}
                                            actions={{
                                                fetchAction: useSearchOrderResulHistoryByProductIdQuery,
                                            }}
                                            appendFilterToUrl={false}
                                            defaultFilter={customFilter}
                                            basePermission={"ORDR"}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Right Column - Diff Viewer */}
                            <div className="flex-grow-1 w-full">
                                <DiffViewer
                                    useDarkTheme={true}
                                    oldValue={getFormattedValue(oldOrderResult)}
                                    newValue={getFormattedValue(newOrderResult)}
                                    compareMethod={DiffMethod.WORDS}
                                    splitView={false}
                                    leftTitle={leftTitle}
                                    rightTitle={rightTitle}
                                    renderContent={(source: string) => (
                                        <pre style={{ color: "white", whiteSpace: "pre-wrap" }}>{source}</pre>
                                    )}
                                />
                            </div>
                        </div>
                    </div>
                </>
            }
        />
    );
};